import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowRight, ArrowUpRight, Search, MapPin, Clock, ShieldCheck,
    Lock, FileText, Network, Play,
} from "lucide-react";
import { api, staticUrl } from "@/lib/api";
import { CommunityPulse } from "@/components/social/CommunityPulse";
import { UniverseCompactCard } from "@/components/social/UniverseCompactCard";
import { PostComposer } from "@/components/social/PostComposer";
import { PostCard } from "@/components/social/PostCard";
import { track } from "@/lib/analytics";

// Cinematic mountain landscape with dramatic light shafts breaking through dense clouds.
// Real photograph — no sci-fi, no UFOs. Composition keeps the light source on the upper-right
// so the UNVEILED wordmark on the left remains unobstructed.
const HERO_BG = "https://images.unsplash.com/photo-1454496522488-7a8e488e8606?auto=format&fit=crop&w=2400&q=70";
// Distant fog/forest ridgeline used as a second layer for additional atmosphere.
const HERO_RIDGE = "https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?auto=format&fit=crop&w=2400&q=70";

function relTime(iso) {
    try {
        const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return `${s}s ago`;
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
        return `${Math.floor(s / 86400)}d ago`;
    } catch { return ""; }
}

export default function Home() {
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [videoOfDay, setVideoOfDay] = useState(null);
    const [featured, setFeatured] = useState(null);
    const [recent, setRecent] = useState([]);
    const [mapEncs, setMapEncs] = useState([]);
    const [connected, setConnected] = useState([]);
    const [species, setSpecies] = useState([]);
    const [timelineDecades, setTimelineDecades] = useState([]);
    const [wallPosts, setWallPosts] = useState([]);
    const [feedItems, setFeedItems] = useState([]);

    useEffect(() => {
        api.get("/media-archive/featured").then((r) => setVideoOfDay(r.data)).catch(() => {});
        api.get("/social/wall", { params: { limit: 4 } })
            .then((r) => setWallPosts(r.data?.items || [])).catch(() => {});
        api.get("/community/feed", { params: { limit: 8 } })
            .then((r) => setFeedItems(r.data?.items || [])).catch(() => {});
        // Featured case: prefer Aguadilla Airport UAP (HD FLIR thumbnail), fallback to most-credible historical
        api.get("/encounters", { params: { q: "Aguadilla", source_type: "historical", limit: 1 } })
            .then((r) => {
                if (r.data.items?.[0]) {
                    setFeatured(r.data.items[0]);
                } else {
                    return api.get("/encounters", { params: { sort: "most_credible", source_type: "historical", limit: 1 } })
                        .then((r2) => setFeatured(r2.data.items?.[0] || null));
                }
            }).catch(() => {});
        api.get("/encounters", { params: { sort: "newest", limit: 6 } })
            .then((r) => setRecent(r.data.items || [])).catch(() => {});
        api.get("/map/encounters", { params: { limit: 200 } })
            .then((r) => setMapEncs(r.data.items || [])).catch(() => {});
        api.get("/species").then((r) => setSpecies((r.data.items || []).slice(0, 8))).catch(() => {});
        api.get("/timeline", { params: { start: 1900, end: 2030 } })
            .then((r) => setTimelineDecades((r.data.items || []))).catch(() => {});
        // Connected cases: pick the highest-credibility historical and pull its similar
        api.get("/encounters", { params: { sort: "most_credible", source_type: "historical", limit: 1 } })
            .then((r) => {
                const root = r.data.items?.[0];
                if (root) {
                    api.get(`/encounters/${root.id}/similar`).then((rr) => setConnected([root, ...(rr.data.items || []).slice(0, 4)]));
                }
            }).catch(() => {});
    }, []);

    const onSearch = (e) => {
        e.preventDefault();
        navigate(`/encounters?q=${encodeURIComponent(q.trim())}`);
    };

    return (
        <div data-testid="home-page" style={{ background: "var(--ok-bg)" }}>
            {/* ============ 1. HERO ============ */}
            <section className="relative overflow-hidden" data-testid="hero">
                {/* Primary cinematic photograph — mountain landscape, dramatic clouds, light shafts on the upper-right */}
                <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "62% 32%", opacity: 0.78, filter: "var(--ok-hero-img-filter)" }} />
                {/* Distant fog ridgeline — softened, blended on top to add depth */}
                <img src={HERO_RIDGE} alt="" className="absolute inset-x-0 bottom-0 w-full h-2/3 object-cover pointer-events-none" style={{ objectPosition: "50% 80%", opacity: 0.28, mixBlendMode: "screen", filter: "var(--ok-hero-ridge-filter)" }} />

                {/* Left-side dark vignette — keeps UNVEILED highly readable on the left */}
                <div className="absolute inset-0 pointer-events-none" style={{ background: "var(--ok-hero-vignette)" }} />
                {/* Vertical depth gradient (top-to-bottom) */}
                <div className="absolute inset-0 pointer-events-none" style={{ background: "var(--ok-hero-vertical)" }} />
                {/* Cold blue-grey tint over the whole scene */}
                <div className="absolute inset-0 pointer-events-none" style={{ background: "var(--ok-hero-tint)", mixBlendMode: "multiply" }} />

                {/* Subtle volumetric light bloom around the natural light source (upper-right) — supports the real shafts, doesn't replace them */}
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 45% 55% at 72% 18%, rgba(190,210,225,0.10) 0%, rgba(155,184,212,0.04) 35%, transparent 65%)" }} />

                {/* Low fog band near the horizon */}
                <div className="absolute inset-x-0 bottom-0 h-2/5 pointer-events-none" style={{ background: "var(--ok-hero-fog)" }} />

                {/* Faint topographic contour overlay — archival map texture */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.05, mixBlendMode: "screen" }} aria-hidden="true">
                    <defs>
                        <pattern id="hero-topo" width="240" height="240" patternUnits="userSpaceOnUse">
                            <path d="M0,140 C40,120 80,150 120,130 C160,110 200,140 240,125" fill="none" stroke="#9bb8d4" strokeWidth="0.5" />
                            <path d="M0,160 C40,142 80,170 120,152 C160,134 200,162 240,148" fill="none" stroke="#9bb8d4" strokeWidth="0.5" />
                            <path d="M0,180 C40,164 80,190 120,174 C160,158 200,184 240,170" fill="none" stroke="#9bb8d4" strokeWidth="0.5" />
                            <path d="M0,100 C40,80 80,108 120,90 C160,72 200,100 240,86" fill="none" stroke="#9bb8d4" strokeWidth="0.5" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#hero-topo)" />
                </svg>
                {/* Reticle / coordinate grid */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.04 }} aria-hidden="true">
                    <defs>
                        <pattern id="hero-grid" width="72" height="72" patternUnits="userSpaceOnUse">
                            <path d="M 72 0 L 0 0 0 72" fill="none" stroke="#9bb8d4" strokeWidth="0.5" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#hero-grid)" />
                </svg>
                {/* Subtle film grain */}
                <div className="absolute inset-0 pointer-events-none" style={{ background: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.6  0 0 0 0 0.7  0 0 0 0 0.8  0 0 0 0.18 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")", opacity: 0.30, mixBlendMode: "overlay" }} />

                <div className="ok-container relative pt-6 pb-10 lg:pt-10 lg:pb-16">
                    <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.4em] opacity-60" data-testid="dossier-id">
                        <Lock className="w-3 h-3 text-[var(--ok-accent)]" />
                        Dossier ID · UNVL-{new Date().getFullYear()}/01 · Classification: Open Archive
                    </div>

                    <div className="mt-6" data-testid="hero-wordmark">
                        <div className="font-mono uppercase text-[8px] tracking-[0.4em] opacity-25 mb-3" style={{ color: "var(--ok-text-muted)" }}>Project</div>
                        <h1 className="font-display font-black tracking-[-0.045em] leading-[0.82] text-[17vw] sm:text-[14vw] lg:text-[12vw] xl:text-[12rem] relative" style={{ letterSpacing: "0.015em", color: "var(--ok-text)", textShadow: "var(--ok-hero-wordmark-shadow), 0 0 90px rgba(155,184,212,0.05) inset" }}>
                            <span style={{ position: "relative", display: "inline-block" }}>
                                UNVEILED
                                {/* extremely subtle centre-weighted archival wash */}
                                <span aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse 55% 38% at 50% 55%, rgba(155,184,212,0.12) 0%, transparent 72%)", mixBlendMode: "overlay" }} />
                                {/* subtle horizontal centre luminosity — very low contrast highlight band */}
                                <span aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(180deg, transparent 30%, rgba(220,232,244,0.07) 50%, transparent 70%)", mixBlendMode: "screen" }} />
                                {/* archival grit/wear texture — very subtle film grain confined to the letterforms */}
                                <span aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='wn'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.05  0 0 0 0 0.07  0 0 0 0 0.10  0 0 0 0.45 0'/></filter><rect width='100%25' height='100%25' filter='url(%23wn)'/></svg>\")", mixBlendMode: "multiply", opacity: 0.18 }} />
                                {/* slight edge fade — top/bottom soft vignette to read like archival lettering */}
                                <span aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(180deg, rgba(10,13,17,0.18) 0%, transparent 18%, transparent 82%, rgba(10,13,17,0.16) 100%)", mixBlendMode: "multiply" }} />
                            </span>
                        </h1>
                    </div>

                    <div className="mt-8 space-y-6">
                        <div className="ok-divider max-w-md" />
                        <p className="font-display text-3xl sm:text-4xl tracking-tight max-w-2xl leading-[1.1]" data-testid="hero-subtitle">
                            The Archive of the Unexplained
                        </p>
                        <p className="text-base sm:text-lg max-w-2xl leading-relaxed" style={{ color: "var(--ok-text-muted)" }} data-testid="hero-tagline">
                            Every witness. Every encounter. Every piece of evidence.<br />
                            <span className="opacity-90 text-[var(--ok-accent)]">Connected. Mapped. Preserved.</span>
                        </p>

                        {/* PROMINENT ARCHIVE SEARCH — full-width row at the top of the grid */}
                        <form onSubmit={onSearch} className="relative group" data-testid="hero-search-form">
                            <div className="relative flex items-stretch" style={{ background: "var(--ok-glass-bg)", border: "1px solid var(--ok-accent)", boxShadow: "0 0 0 4px rgba(155,184,212,0.06), 0 12px 40px rgba(0,0,0,0.18)" }}>
                                <Search className="w-5 h-5 absolute left-5 top-1/2 -translate-y-1/2 text-[var(--ok-accent)]" />
                                <input value={q} onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search the archive: witness, year, location, entity…"
                                    className="flex-1 bg-transparent pl-14 pr-4 py-5 text-lg outline-none placeholder:opacity-50"
                                    style={{ color: "var(--ok-on-glass)" }}
                                    data-testid="hero-search-input" />
                                <button type="submit" className="px-6 font-mono text-[11px] uppercase tracking-[0.3em] flex items-center gap-2 transition-colors" style={{ background: "var(--ok-accent)", color: "var(--ok-on-accent)" }} data-testid="hero-search-submit">
                                    Query Archive <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] opacity-50">
                                Witness accounts · Historical records · Military encounters · Worldwide reports
                            </div>
                        </form>

                        {/* TWO-COLUMN FEATURE GRID — archive intelligence (left) + video of the day (right), edges aligned */}
                        <div className="grid lg:grid-cols-12 gap-6 items-stretch" data-testid="hero-feature-grid">
                            <div className="lg:col-span-7 order-2 lg:order-1 min-w-0">
                                {/* CONNECTED ARCHIVE INTELLIGENCE */}
                                <div className="ok-card p-5 lg:p-6 h-full flex flex-col" data-testid="connected-intel-block" style={{ background: "var(--ok-glass-bg-strong)", borderColor: "var(--ok-glass-border)", backdropFilter: "blur(8px)" }}>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[var(--ok-accent)] flex items-center gap-2">
                                        <Network className="w-3 h-3" /> Connected Archive Intelligence
                                    </div>
                                    <p className="mt-3 text-sm sm:text-base leading-relaxed opacity-85 max-w-2xl">
                                        Every record is cross-referenced against the entire archive. Entities, locations, dates, evidence indices and witness signatures are compared to surface patterns no single report could reveal.
                                    </p>
                                    <div className="mt-auto pt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                                        <IntelRow icon={<Network className="w-3 h-3" />} k="Entity match" v="Compares observed species, craft and signatures across cases." />
                                        <IntelRow icon={<MapPin className="w-3 h-3" />} k="Geospatial link" v="Surfaces nearby reports and recurring hotspots." />
                                        <IntelRow icon={<Clock className="w-3 h-3" />} k="Temporal cluster" v="Detects encounter waves across decades and centuries." />
                                    </div>
                                </div>
                            </div>
                            <div className="lg:col-span-5 order-1 lg:order-2 min-w-0">
                                <VideoOfTheDay video={videoOfDay} fill />
                            </div>
                        </div>

                        {/* SUPPORT ROW — CTAs under the left column · 75+ Years of Data anchored under the video */}
                        <div className="grid lg:grid-cols-12 gap-6 items-start">
                            <div className="lg:col-span-7 flex flex-wrap gap-3 pt-2">
                                <Link to="/encounters" onClick={() => track("cta_clicked", { cta_id: "hero_explore_archive", label: "Explore the Archive", location: "home_hero" })} className="ok-btn-primary" data-testid="hero-explore-btn">
                                    Explore the Archive <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/lodge" onClick={() => track("cta_clicked", { cta_id: "hero_submit_encounter", label: "Submit an Encounter", location: "home_hero" })} className="ok-btn-outline" data-testid="hero-submit-btn">
                                    Submit an Encounter <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </div>
                            <div className="lg:col-span-5 select-none pointer-events-none hidden lg:block" data-testid="years-of-data-feature" aria-hidden="true">
                                <div className="ok-divider mb-4" style={{ opacity: 0.5 }} />
                                <div className="flex items-end justify-between gap-4">
                                    <div className="font-display font-black leading-[0.8]"
                                        style={{ fontSize: "clamp(4rem, 5vw, 6rem)", opacity: 0.35, letterSpacing: "-0.03em", color: "var(--ok-text)", textShadow: "0 1px 30px rgba(155,184,212,0.10)" }}>
                                        75+
                                    </div>
                                    <div className="font-mono uppercase tracking-[0.45em] text-right pb-1.5"
                                        style={{ fontSize: "0.8rem", opacity: 0.35, color: "var(--ok-accent)", lineHeight: 2 }}>
                                        Years<br />of Data
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ 2b. LIVE DROPS + COMMUNITY FEED ============ */}
            <section className="ok-container py-12 lg:py-14" data-testid="live-community-section">
                <SectionHead eyebrow="Section 02b · Happening now" title="Live drops & community feed" link="/community" linkLabel="Open the main wall" />
                <div className="grid lg:grid-cols-12 gap-8 mt-6 items-start">
                    <div className="lg:col-span-7 space-y-3 min-w-0" data-testid="home-live-drop">
                        <PostComposer compact onPosted={(p) => setWallPosts((prev) => [p, ...prev].slice(0, 4))} />
                        {wallPosts.length === 0 ? (
                            <div className="ok-card p-6 text-sm opacity-70 text-center">Nothing on the wall yet. Be the first to drop a sighting.</div>
                        ) : wallPosts.map((p) => (
                            <PostCard key={p.id} post={p} compact onRemoved={(id) => setWallPosts((prev) => prev.filter((x) => x.id !== id))} />
                        ))}
                        <Link to="/community" className="ok-btn-outline w-fit text-xs" data-testid="home-wall-more">
                            See the full wall <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                    <aside className="lg:col-span-5 min-w-0" data-testid="home-feed-preview">
                        <div className="ok-card p-5" style={{ background: "var(--ok-glass-bg-strong)", borderColor: "var(--ok-glass-border)" }}>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)] mb-3">Latest across the archive</div>
                            <div className="space-y-3">
                                {feedItems.length === 0 && <div className="text-xs opacity-50 font-mono">Listening for activity…</div>}
                                {feedItems.slice(0, 7).map((i) => (
                                    <FeedPreviewRow key={`${i.kind}-${i.id}`} item={i} />
                                ))}
                            </div>
                            <Link to="/community" className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-70 hover:text-[var(--ok-accent)] flex items-center gap-1.5 mt-4">
                                Full activity feed <ArrowUpRight className="w-3 h-3" />
                            </Link>
                        </div>
                    </aside>
                </div>
            </section>

            {/* ============ 3. COMMUNITY PULSE ============ */}
            <CommunityPulse />

            {/* ============ 4. FEATURED ENCOUNTER ============ */}
            {featured && (
                <section className="ok-container py-14 lg:py-16" data-testid="featured-encounter-section">
                    <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[var(--ok-accent)] mb-2 flex items-center gap-2" data-testid="featured-encounter-eyebrow">
                                <ShieldCheck className="w-3 h-3" /> Section 03 · Featured case · Highest evidence index
                            </div>
                            <h2 className="font-display text-2xl sm:text-3xl leading-tight">From the archive</h2>
                        </div>
                        <Link to="/encounters?sort=most_credible" className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-70 hover:text-[var(--ok-accent)] flex items-center gap-1.5">
                            Browse highest-credibility cases <ArrowUpRight className="w-3 h-3" />
                        </Link>
                    </div>
                    <div className="grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-7">
                        <Link to={`/encounters/${featured.id}`} className="block ok-card overflow-hidden group relative" data-testid="featured-encounter-card" style={{ background: "var(--ok-image-frame)" }}>
                            <div className="aspect-[16/10] overflow-hidden relative">
                                <img src={staticUrl(featured.sketch_url) || HERO_BG} alt={featured.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" style={{ filter: "grayscale(100%) contrast(1.18) brightness(0.78) sepia(0.18) hue-rotate(180deg) saturate(0.7)" }} />
                                {/* Archival blue-grey tint to read like a declassified photograph */}
                                <div className="absolute inset-0 pointer-events-none" style={{ background: "var(--ok-archival-tint)", mixBlendMode: "multiply" }} />
                                {/* Film-grain overlay */}
                                <div className="absolute inset-0 pointer-events-none" style={{ background: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.1' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.55  0 0 0 0 0.65  0 0 0 0 0.78  0 0 0 0.32 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")", opacity: 0.55, mixBlendMode: "overlay" }} />
                                {/* Subtle scan-line texture */}
                                <div className="absolute inset-0 pointer-events-none" style={{ background: "repeating-linear-gradient(0deg, rgba(255,255,255,0.018) 0px, rgba(255,255,255,0.018) 1px, transparent 1px, transparent 3px)" }} />
                                {/* Corner registration marks */}
                                <svg className="absolute top-3 right-3 w-4 h-4 opacity-50" viewBox="0 0 16 16" aria-hidden><path d="M0 4V0H4M12 0H16V4M16 12V16H12M4 16H0V12" stroke="#9bb8d4" strokeWidth="1" fill="none"/></svg>
                                <svg className="absolute bottom-3 left-3 w-4 h-4 opacity-50" viewBox="0 0 16 16" aria-hidden><path d="M0 4V0H4M12 0H16V4M16 12V16H12M4 16H0V12" stroke="#9bb8d4" strokeWidth="1" fill="none"/></svg>
                                {/* Archival evidence label (top-left) */}
                                <div className="absolute top-4 left-4 font-mono text-[9px] uppercase tracking-[0.3em] px-2.5 py-1.5 flex items-center gap-2" style={{ background: "var(--ok-chip-overlay-bg)", border: "1px solid var(--ok-chip-overlay-border)", color: "var(--ok-chip-overlay-color)" }}>
                                    <FileText className="w-3 h-3" /> {featured.image_label || "Archival exhibit"}
                                </div>
                            </div>
                            {/* Archival metadata strip */}
                            <div className="px-4 py-2.5 flex items-center justify-between gap-4 font-mono text-[10px] uppercase tracking-[0.25em] border-t" style={{ borderColor: "var(--ok-border)", background: "var(--ok-archival-meta-bg)", color: "#c4d8ea" }}>
                                <span>File · UNVL-{(featured.id || "00000000").slice(0, 8)}</span>
                                <span className="opacity-70">{(featured.encounter_date || "").slice(0, 10) || "Date unknown"}</span>
                                <span className="opacity-70">{featured.country || featured.location || "Location restricted"}</span>
                                <span style={{ color: "#9bb8d4" }}>{featured.source_category || "Archival record"}</span>
                            </div>
                        </Link>
                    </div>
                    <div className="lg:col-span-5 flex flex-col justify-center space-y-5">
                        <h3 className="font-display text-3xl sm:text-4xl leading-tight" data-testid="featured-encounter-title">{featured.title}</h3>
                        <div className="flex flex-wrap gap-2 text-xs">
                            <span className="ok-tag">{(featured.encounter_date || "").slice(0, 10) || "Unknown"}</span>
                            <span className="ok-tag">{featured.location || featured.country}</span>
                            {featured.encounter_type && <span className="ok-tag">{featured.encounter_type}</span>}
                            {featured.source_category && <span className="ok-tag" style={{ color: "var(--ok-accent)", borderColor: "var(--ok-accent)" }}>{featured.source_category}</span>}
                        </div>
                        <p className="text-sm opacity-80 leading-relaxed line-clamp-6">
                            {featured.ai_summary || "An archival case in the Unveiled record."}
                        </p>
                        <Link to={`/encounters/${featured.id}`} className="ok-btn-primary w-fit" data-testid="featured-encounter-open">
                            Open dossier <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    </div>
                </section>
            )}

            {/* ============ 4. RECENT ENCOUNTERS ============ */}
            <section className="ok-container py-12" data-testid="recent-encounters-section">
                <SectionHead eyebrow="Section 04 · Newly indexed" title="Recent encounters" link="/encounters?sort=newest" linkLabel="Browse all" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                    {recent.slice(0, 6).map((e) => (
                        <Link key={e.id} to={`/encounters/${e.id}`} className="ok-card ok-card-hover overflow-hidden group" data-testid={`recent-${e.id}`}>
                            <div className="aspect-[16/9] overflow-hidden relative" style={{ background: "#0a0d11" }}>
                                <img src={staticUrl(e.sketch_url) || HERO_BG} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ filter: "grayscale(95%) contrast(1.1) brightness(0.82) sepia(0.10) hue-rotate(180deg) saturate(0.6)" }} />
                                {/* archival tint + grain */}
                                <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(20,30,42,0.15) 0%, rgba(10,13,17,0.45) 100%)", mixBlendMode: "multiply" }} />
                                <div className="absolute inset-0 pointer-events-none" style={{ background: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n2'><feTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.55  0 0 0 0 0.65  0 0 0 0 0.78  0 0 0 0.22 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n2)'/></svg>\")", opacity: 0.5, mixBlendMode: "overlay" }} />
                                {/* file no chip */}
                                <div className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5" style={{ background: "rgba(10,13,17,0.85)", border: "1px solid rgba(155,184,212,0.3)", color: "#c4d8ea" }}>
                                    UNVL-{(e.id || "00000000").slice(0, 6)}
                                </div>
                                {e.image_label && (
                                    <div className="absolute bottom-2 left-2 font-mono text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5" style={{ background: "rgba(10,13,17,0.85)", color: "#9bb8d4" }}>
                                        {e.image_label}
                                    </div>
                                )}
                            </div>
                            <div className="p-4 space-y-2">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-50">
                                    {(e.encounter_date || "").slice(0, 10) || "Unknown date"} · {e.country || "—"}
                                </div>
                                <div className="font-display font-bold text-base leading-tight line-clamp-2 group-hover:text-[var(--ok-accent)] transition-colors">{e.title}</div>
                                <div className="flex items-center gap-3 text-[10px] opacity-60 font-mono uppercase tracking-[0.2em]">
                                    <span className="flex items-center gap-1"><ShieldCheck className="inline w-3 h-3 text-[var(--ok-accent)]" /> {e.source_category || "Witness Testimony"}</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* ============ 5. GLOBAL MAP PREVIEW ============ */}
            <section className="ok-container py-12 grid lg:grid-cols-12 gap-8 items-center" data-testid="map-preview-section">
                <div className="lg:col-span-5 space-y-4">
                    <SectionHead eyebrow="Section 05 · Geospatial index" title="Global encounter map" />
                    <p className="text-sm opacity-75 leading-relaxed max-w-md">
                        Every documented encounter pinned to its reported location. Filter by entity, type, decade or evidence index. Patterns emerge across geography.
                    </p>
                    <Link to="/map" className="ok-btn-outline w-fit" data-testid="map-preview-open">
                        Open the world map <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="lg:col-span-7">
                    <Link to="/map" className="ok-card block relative aspect-[16/9] overflow-hidden group" data-testid="map-preview-canvas" style={{ background: "var(--ok-image-frame)" }}>
                        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/83/Equirectangular_projection_SW.jpg/1280px-Equirectangular_projection_SW.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-15 mix-blend-screen pointer-events-none" />
                        <svg viewBox="0 0 800 400" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
                            {mapEncs.slice(0, 80).map((e, i) => {
                                const cx = ((e.lng + 180) / 360) * 800;
                                const cy = ((90 - e.lat) / 180) * 400;
                                return (
                                    <g key={i}>
                                        <circle cx={cx} cy={cy} r="6" fill="#9bb8d4" opacity="0.12" />
                                        <circle cx={cx} cy={cy} r="2.2" fill="#c4d8ea">
                                            <animate attributeName="opacity" values="0.35;0.9;0.35" dur={`${2 + (i % 4)}s`} repeatCount="indefinite" />
                                        </circle>
                                    </g>
                                );
                            })}
                        </svg>
                        <div className="absolute bottom-3 right-3 font-mono text-[10px] uppercase tracking-[0.3em] opacity-60 group-hover:text-[var(--ok-accent)] transition-colors">
                            Open the global map →
                        </div>
                    </Link>
                </div>
            </section>

            {/* ============ 6. CONNECTED CASES ============ */}
            {connected.length > 1 && (
                <section className="ok-container py-12" data-testid="connected-cases-section">
                    <SectionHead eyebrow="Section 06 · Cross-case graph" title="Connected cases" link={`/encounters/${connected[0].id}/matches`} linkLabel="Explore matching engine" />
                    <p className="text-sm opacity-70 max-w-2xl mt-2">Reports linked by entity, location, evidence and consciousness markers. Patterns become visible when you compare them side by side.</p>
                    <div className="mt-8 ok-card p-6 lg:p-8" data-testid="connected-graph">
                        <div className="flex items-start gap-4 flex-wrap">
                            <div className="flex-1 min-w-[260px]">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)] flex items-center gap-2 mb-2"><Network className="w-3 h-3" /> Source case</div>
                                <Link to={`/encounters/${connected[0].id}`} className="font-display text-xl leading-tight hover:text-[var(--ok-accent)]">
                                    {connected[0].title}
                                </Link>
                                <div className="text-xs opacity-60 mt-1">{(connected[0].encounter_date || "").slice(0, 10)} · {connected[0].country}</div>
                            </div>
                            <ArrowRight className="w-5 h-5 text-[var(--ok-accent)] mt-6 hidden lg:block" />
                            <div className="flex-[2] grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-[280px]">
                                {connected.slice(1, 5).map((c) => (
                                    <Link key={c.id} to={`/encounters/${c.id}`} className="ok-card ok-card-hover p-3 group" data-testid={`connected-${c.id}`}>
                                        <div className="font-mono text-[9px] uppercase tracking-[0.3em] opacity-50 mb-1">{(c.encounter_date || "").slice(0, 10) || "—"} · {c.country || "—"}</div>
                                        <div className="text-sm font-display font-bold leading-tight line-clamp-2 group-hover:text-[var(--ok-accent)]">{c.title}</div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ============ 7. SPECIES & ENTITY ARCHIVE ============ */}
            <section className="ok-container py-12" data-testid="species-section">
                <SectionHead eyebrow="Section 07 · Catalogue" title="Species & entity archive" link="/species" linkLabel="Open encyclopedia" />
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-4 mt-6">
                    {species.slice(0, 8).map((s) => (
                        <Link key={s.slug} to={`/species/${s.slug}`} className="ok-card ok-card-hover overflow-hidden group" data-testid={`species-tile-${s.slug}`}>
                            <div className="aspect-square overflow-hidden">
                                <img src={s.image_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ filter: "grayscale(40%) contrast(1.05)" }} />
                            </div>
                            <div className="p-3">
                                <div className="font-display font-bold text-sm leading-tight">{s.name}</div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-50 mt-1">
                                    Catalogued entity
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* ============ 7b. UNIVERSE EXPLORER (compact CTA) ============ */}
            <UniverseCompactCard />

            {/* ============ 8. TIMELINE PREVIEW ============ */}
            <section className="ok-container py-12" data-testid="timeline-section">
                <SectionHead eyebrow="Section 08 · Chronology" title="Timeline of the unexplained" link="/timeline" linkLabel="Open timeline" />
                <p className="text-sm opacity-70 max-w-2xl mt-2">Encounter density by decade. Waves emerge across the modern UFO era and the centuries preceding it.</p>
                <div className="ok-card p-6 mt-6 overflow-x-auto" data-testid="timeline-strip">
                    <div className="flex items-end gap-1.5 min-w-max">
                        {timelineDecades.length === 0 && (
                            <div className="text-xs opacity-50 py-8">Loading chronology…</div>
                        )}
                        {timelineDecades.map((d) => (
                            <Link key={d.decade} to={`/timeline#decade-${d.decade}`} className="text-center group" data-testid={`timeline-decade-${d.decade}`}>
                                <div className="font-mono text-[10px] opacity-60 mb-1">{d.decade}s</div>
                                <div className="w-12 transition-all group-hover:opacity-100"
                                    style={{
                                        height: `${Math.min(160, 12 + d.count * 5)}px`,
                                        background: "linear-gradient(180deg, var(--ok-accent) 0%, var(--ok-surface-hover) 100%)",
                                        borderRadius: "2px",
                                        opacity: 0.85,
                                    }} />
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ 9. SUBMIT CTA ============ */}
            <section className="ok-container py-20" data-testid="submit-cta-section">
                <div className="ok-card p-10 lg:p-16 relative overflow-hidden" style={{ background: "linear-gradient(180deg, var(--ok-surface) 0%, var(--ok-bg) 100%)" }}>
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.05 }}>
                        <rect width="100%" height="100%" fill="url(#hero-grid)" />
                    </svg>
                    <div className="relative max-w-2xl">
                        <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[var(--ok-accent)] flex items-center gap-2">
                            <Lock className="w-3 h-3" /> Section 09 · Add to the record
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
                            Every witness counts.<br />Add yours to the record.
                        </h2>
                        <p className="text-base opacity-75 mt-4 leading-relaxed">
                            Anonymous or under your name. Photo evidence, audio, written testimony. Preserved, indexed, and connected to every related case across time and place. No theory, no judgement. Just the record.
                        </p>
                        <div className="flex flex-wrap gap-3 mt-8">
                            <Link to="/lodge" className="ok-btn-primary" data-testid="submit-cta-primary">
                                Submit an encounter <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link to="/about" className="ok-btn-outline" data-testid="submit-cta-secondary">
                                How the archive works
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function VideoOfTheDay({ video, fill }) {
    const [playing, setPlaying] = useState(false);
    if (!video) return null;
    const inner = playing && video.video_url ? (
        <video
            controls
            autoPlay
            playsInline
            className="absolute inset-0 w-full h-full object-contain bg-black"
            data-testid="video-of-the-day-player"
        >
            {(video.video_sources?.length ? video.video_sources : [{ src: video.video_url, type: undefined }]).map((s) => (
                <source key={s.src} src={staticUrl(s.src)} type={s.type || undefined} />
            ))}
        </video>
    ) : (
        <button type="button" onClick={() => video.video_url && setPlaying(true)}
            className="absolute inset-0 w-full h-full text-left cursor-pointer"
            data-testid="video-of-the-day-play" aria-label={`Play ${video.title}`}>
            <img src={staticUrl(video.thumbnail_url)} alt={video.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
            {/* cinematic gradient */}
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(10,13,17,0.30) 0%, transparent 35%, rgba(10,13,17,0.85) 100%)" }} />
            {/* subtle scan lines */}
            <div className="absolute inset-0 pointer-events-none" style={{ background: "repeating-linear-gradient(0deg, rgba(255,255,255,0.015) 0px, rgba(255,255,255,0.015) 1px, transparent 1px, transparent 3px)" }} />
            {/* eyebrow label */}
            <div className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.3em] px-2.5 py-1.5 flex items-center gap-2" style={{ background: "rgba(10,13,17,0.85)", border: "1px solid rgba(155,184,212,0.35)", color: "#c4d8ea" }} data-testid="video-of-the-day-label">
                <Play className="w-3 h-3 text-[var(--ok-accent)]" /> Video of the Day
            </div>
            {video.classification && (
                <div className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1" style={{ background: "rgba(10,13,17,0.85)", color: "#9bb8d4" }}>
                    {video.classification}
                </div>
            )}
            {/* central play button */}
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                    style={{ background: "rgba(10,13,17,0.65)", border: "1px solid rgba(196,216,234,0.5)", backdropFilter: "blur(6px)", boxShadow: "0 0 40px rgba(155,184,212,0.25)" }}>
                    <Play className="w-6 h-6 ml-0.5" style={{ color: "#dce8f4", fill: "#dce8f4" }} />
                </div>
            </div>
            {/* title on image */}
            <div className="absolute inset-x-0 bottom-0 p-4">
                <div className="font-display font-bold text-base leading-tight group-hover:text-[var(--ok-accent)] transition-colors" style={{ color: "#e8f0f8" }} data-testid="video-of-the-day-title">
                    {video.title}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] mt-1.5" style={{ color: "rgba(196,216,234,0.65)" }}>
                    {video.location || "Location classified"} · Watch the footage →
                </div>
            </div>
        </button>
    );
    return (
        <div className={`ok-card overflow-hidden group relative flex flex-col ${fill ? "lg:h-full" : ""}`}
            data-testid="video-of-the-day-card"
            style={{ background: "var(--ok-image-frame)", borderColor: "var(--ok-glass-border)", boxShadow: "0 18px 50px rgba(0,0,0,0.35)" }}>
            <div className="relative w-full aspect-video lg:aspect-auto lg:flex-1 lg:min-h-[280px]">
                {inner}
            </div>
        </div>
    );
}

function FeedPreviewRow({ item }) {
    const kindLabel = {
        post: "dropped to the wall",
        encounter: "new encounter filed",
        comment: "commented",
        media: "uploaded media",
    }[item.kind] || "activity";
    const to = item.kind === "encounter" ? `/encounters/${item.id}`
        : item.kind === "comment" && item.target_type === "encounter" ? `/encounters/${item.target_id}`
        : "/community";
    const who = item.user_name || item.author_name || "Witness";
    const text = item.kind === "encounter" ? item.title : (item.body || item.filename || "");
    return (
        <Link to={to} className="block group" data-testid={`feed-preview-${item.kind}-${item.id}`}>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] opacity-50 flex items-center gap-1.5">
                <span className="text-[var(--ok-accent)]">{who}</span> · {kindLabel} · {relTime(item.ts || item.created_at)}
            </div>
            {text && <div className="text-xs opacity-80 mt-0.5 line-clamp-2 group-hover:text-[var(--ok-accent)] transition-colors">{text}</div>}
        </Link>
    );
}

function IntelRow({ icon, k, v }) {
    return (
        <div className="flex flex-col gap-1.5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)] flex items-center gap-1.5">
                {icon}{k}
            </div>
            <div className="text-xs opacity-70 leading-snug">{v}</div>
        </div>
    );
}

function SectionHead({ eyebrow, title, link, linkLabel }) {
    return (
        <div className="flex items-end justify-between gap-4 flex-wrap" data-testid="section-head">
            <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[var(--ok-accent)] mb-2">{eyebrow}</div>
                <h2 className="font-display text-2xl sm:text-3xl leading-tight">{title}</h2>
            </div>
            {link && (
                <Link to={link} className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-70 hover:text-[var(--ok-accent)] flex items-center gap-1.5">
                    {linkLabel} <ArrowUpRight className="w-3 h-3" />
                </Link>
            )}
        </div>
    );
}
